import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import App from './App.tsx'
import './index.css'
import { Viewer, XKTLoaderPlugin } from '@xeokit/xeokit-sdk'
import { MessageList } from './MessageList.ts'
import { ObjectSelection } from './ObjectSelection.ts'
import { setXeokit } from './XeokitSingleton.ts'
import { AiMessageHandler } from './AiMessageHandler'
import { BusyIndicator } from './BusyIndicator.ts'

import { setSqlite } from './SqliteSingleton.ts'
import './testIsolation.ts' // Import test functions for global access

const initDb = async (dbUrl: string) => {
  // Initialize sql.js
  const initSqlJs = (await import('sql.js')).default;
  const SQL = await initSqlJs({
    locateFile: file => `public/${file}`
  });

  // Load your existing database file
  const dbFileArrayBuffer = await fetch(dbUrl).then(res => res.arrayBuffer());

  // Create a database instance
  const db = new SQL.Database(new Uint8Array(dbFileArrayBuffer));

  return db;
};

(async () => {
  const db = await initDb('public/Duplex.sqlite');

  setSqlite(db);

  const canvasElement = document.getElementById('xeokit-canvas') as HTMLCanvasElement;

  const viewer = new Viewer({
    canvasElement,
    transparent: true,
    antialias: true,
  });

  setXeokit(viewer);

  const loader = new XKTLoaderPlugin(viewer, {
    objectDefaults: {
      // @ts-ignore
      DEFAULT: null,
      IfcSpace: {
        pickable: false,
        visible: false,
      }
    }
  });

  // Add a check for the XKT file
  //const xktFilePath = '/Duplex.xkt';
  //const xktFilePath = '/onewall.xkt';
  //const xktFilePath = 'https://dl.dropboxusercontent.com/scl/fi/t1vew9ge9cjs3cka07ner/Duplex.xkt?rlkey=0994bomkoj3nigctqwzi2daek';
  const xktFilePath = 'https://dl.dropboxusercontent.com/scl/fi/j4u90m2ovl7606mbm2y0k/rac_basic_sample_project_Test_3D.xkt?rlkey=sg5sizke90twkm6msspgzxsmi&st=mj6h28al';
  //const modelName = 'rac_basic_sample_project_Test.rvt';
  try {
    const response = await fetch(xktFilePath);
    if (!response.ok) {
      console.error(`Failed to fetch ${xktFilePath}: ${response.status} ${response.statusText}`);
      // You might want to add more robust error handling here, e.g., display a message to the user
    } else {
      console.log(`${xktFilePath} found. Proceeding to load.`);

      const model = loader.load({
        id: 'my-model',
        src: xktFilePath
      });

      model.on('loaded', () => viewer.cameraFlight.flyTo());

      let canvasPos = [0, 0];

      canvasElement.addEventListener('mousedown', ev => {
        const rect = canvasElement.getBoundingClientRect();
        canvasPos = [ev.clientX - rect.left, ev.clientY - rect.top];
      });

      const objectSelection = new ObjectSelection();

      canvasElement.addEventListener('mouseup', ev => {
        const rect = canvasElement.getBoundingClientRect();
        const currentPos = [ev.clientX - rect.left, ev.clientY - rect.top];
        
        if (Math.abs(currentPos[0] - canvasPos[0]) > 10) return;
        if (Math.abs(currentPos[1] - canvasPos[1]) > 10) return;

        model.entityList.forEach (e => e.highlighted = false);

        const entity = viewer.scene.pick({canvasPos: currentPos })?.entity ?? null;

        if (entity) {
          entity.highlighted = true;
        }

        objectSelection.setSelectedObject(entity);
      });

      const messageList = new MessageList();

      let versionText = 'Labs ChatBIM (version unknown)';
      try {
        const healthRes = await fetch('/api/health');
        if (healthRes.ok) {
          const health = await healthRes.json();
          if (health?.version) versionText = health.version;
        }
      } catch {
        // Use fallback if health check fails
      }

      messageList.addMessage({
        id: "1",
        text: versionText,
        who: 'system'
      });

      messageList.addMessage({
        id: "2",
        text: `You can ask any question you have about the model:
- Query the model (e.g., "Find the biggest wall in the model")
- Adjust visibility (e.g., "Isolate the wall with the largest area")
- Reset the view (e.g., "Restore all objects")
- Inspect properties (e.g., "Show me the properties of the selected object")
- More to explore...
`,
        who: 'system'
      });

      const busyIndicator = new BusyIndicator();
      new AiMessageHandler(messageList, busyIndicator, objectSelection);

      createRoot(document.getElementById('react-root')!).render(
        <StrictMode>
          <App messageList={messageList} objectSelection={objectSelection} busyIndicator={busyIndicator}/>
        </StrictMode>,
      )
    }
  } catch (error) {
    console.error('Error loading XKT file:', error);
  }
})()